import { computed, inject, Injectable, PLATFORM_ID, signal, TransferState, makeStateKey } from '@angular/core';
import { SettingsResponse } from './models/settings-response';
import { API_URL } from '@app/core/tokens/api.token';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { StorageService } from '../storage/storage.service';
import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import { DomainResolverService } from '../domain-resolver.service';
import { TRANSFER_STATE_KEYS } from '@app/core/constants/transfer-state-keys';

const HOMEPAGE_SETTINGS_STORAGE_KEY = 'setting' as const;
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes in milliseconds
const transferStateSettings = TRANSFER_STATE_KEYS.HOMEPAGE_SETTINGS;

// Server-side per-domain cache
interface SettingsCache {
  data: SettingsResponse;
  expires: number;
}

// Per-domain cache for multiple tenants
const serverSettingsCache = new Map<string, SettingsCache>();

// Per-domain pending requests to prevent duplicate fetches
const serverPendingRequests = new Map<string, Promise<void>>();

@Injectable({ providedIn: 'root' })
export class SettingsService {
  apiUrl = inject(API_URL);
  http = inject(HttpClient);
  localStorage = inject(StorageService);
  domainResolver = inject(DomainResolverService);
  transferState = inject(TransferState);
  settings = signal<SettingsResponse | null>(null);
  isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  isServer = isPlatformServer(inject(PLATFORM_ID));

  // Computed values for common homepage settings
  readonly tcDomain = computed(() => {
    const domain = this.settings()?.tc_domain as string;
    return domain && domain !== '' ? domain.replace(/\/$/, '') : '';
  });
  readonly agencyName = computed(() => {
    return (this.settings()?.agency_name as string) || '';
  });
  readonly agencyDomain = computed(() => {
    return (this.settings()?.tc_domain as string) || '';
  });
  readonly isProposalDomain = computed(() => {
    return this.settings()?.agency?.has_home_feature_products === 1;
  });

  async loadSettings(): Promise<void> {
    // 1. Fast path: Signal already populated (hot cache)
    if (this.settings()) {
      return;
    }

    // 2. Client-side: Check TransferState first (from SSR)
    if (this.isBrowser && this.transferState.hasKey(transferStateSettings)) {
      const transferredSettings = this.transferState.get(transferStateSettings, null);
      if (transferredSettings) {
        this.settings.set(transferredSettings);
        return;
      }
    }

    // 3. Server-side path: Multi-tenant domain-based caching
    if (this.isServer) {
      const currentDomain = this.domainResolver.getHostname();

      // Check if there's a pending request for this domain - reuse it
      const pendingRequest = serverPendingRequests.get(currentDomain);
      if (pendingRequest) {
        await pendingRequest;
        return;
      }

      // Check server-side cache with TTL
      const cached = serverSettingsCache.get(currentDomain);
      if (cached && Date.now() < cached.expires) {
        this.settings.set(cached.data);
        return;
      }

      // Create new fetch promise and register it to prevent duplicate requests
      const fetchPromise = this.fetchSettings();
      serverPendingRequests.set(currentDomain, fetchPromise);

      try {
        await fetchPromise;
      } finally {
        // Always cleanup the pending request to prevent memory leaks
        serverPendingRequests.delete(currentDomain);
      }
      return;
    }

    // 4. Browser path: localStorage caching
    const savedSettings = this.localStorage.getItem(HOMEPAGE_SETTINGS_STORAGE_KEY);
    if (savedSettings) {
      try {
        const parsedSettings = JSON.parse(savedSettings) as SettingsResponse;
        this.settings.set(parsedSettings);
        return;
      } catch (error) {
        console.error('Failed to parse cached settings:', error);
      }
    }

    // 5. No cache available - fetch fresh data
    await this.fetchSettings();
  }

  private async fetchSettings(): Promise<void> {
    try {
      // Using firstValueFrom to convert Observable to Promise and avoid subscription
      const settings = await firstValueFrom(this.http.get<SettingsResponse>(`${this.apiUrl}/homepage-settings`));

      if (settings) {
        this.settings.set(settings);
      }

      // Server-side: Cache per-domain with TTL and auto-cleanup
      if (this.isServer) {
        const currentDomain = this.domainResolver.getHostname();
        serverSettingsCache.set(currentDomain, {
          data: settings,
          expires: Date.now() + CACHE_TTL,
        });

        // Store in TransferState for client hydration
        this.transferState.set(transferStateSettings, settings);

        // Efficient cleanup: only when cache exceeds threshold
        if (serverSettingsCache.size > 100) {
          const now = Date.now();
          for (const [domain, entry] of serverSettingsCache.entries()) {
            if (now >= entry.expires) {
              serverSettingsCache.delete(domain);
            }
          }
        }
      } else {
        // Browser-side: Cache in localStorage
        this.localStorage.setItem(HOMEPAGE_SETTINGS_STORAGE_KEY, JSON.stringify(settings));
      }
    } catch (error) {
      console.error('Failed to fetch settings:', error);

      // Fallback to stale cache on server if available
      if (this.isServer) {
        const currentDomain = this.domainResolver.getHostname();
        const cached = serverSettingsCache.get(currentDomain);
        if (cached) {
          console.warn('Using stale cached settings after fetch error for domain:', currentDomain);
          this.settings.set(cached.data);
        }
      }
    }
  }

  async refreshSettings(): Promise<void> {
    // Clear the current signal state to force a fresh fetch
    this.settings.set(null);

    // Clear browser localStorage cache
    if (this.isBrowser) {
      this.localStorage.removeItem(HOMEPAGE_SETTINGS_STORAGE_KEY);
    }

    // Clear server-side cache for current domain
    if (this.isServer) {
      const currentDomain = this.domainResolver.getHostname();
      serverSettingsCache.delete(currentDomain);
    }

    // Fetch fresh settings
    await this.fetchSettings();
  }
}
